// src/support/SupportLicenseGate.js
// License-aware wrapper that gates support features behind module purchase.
// Checks the license API; if unlicensed, shows a purchase prompt instead of the UI.

import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const MODULE_ID = 'support';

const FEATURES = [
  'Unlimited support tickets',
  'Admin ticket management & routing',
  'Priority & status management',
  'Comment threads',
  'Support analytics & stats',
];

/**
 * Check if merchant has access to the support module.
 * @param {string} merchantWallet
 * @returns {Promise<{hasAccess: boolean, error?: string}>}
 */
export async function checkSupportLicense(merchantWallet) {
  if (!merchantWallet) return { hasAccess: false, error: 'No wallet' };
  try {
    const res = await fetch(
      `${API_BASE}/api/license/check-feature?merchantWallet=${merchantWallet}&feature=${MODULE_ID}`
    );
    if (!res.ok) throw new Error('License check failed');
    const data = await res.json();
    return { hasAccess: !!data.hasAccess };
  } catch (err) {
    return { hasAccess: false, error: err.message };
  }
}

/**
 * Purchase the support module for a merchant.
 * @param {string} merchantWallet
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function purchaseSupportModule(merchantWallet) {
  try {
    const res = await fetch(`${API_BASE}/api/license/purchase-module`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ merchantWallet, module: MODULE_ID, price: 150 }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Purchase failed');
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Hook that checks support module license status.
 * @param {string} merchantWallet
 * @returns {{ hasAccess: boolean, isLoading: boolean, error: string|null, refresh: function, purchase: function }}
 */
export function useSupportLicense(merchantWallet) {
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    if (!merchantWallet) { setIsLoading(false); return; }
    setIsLoading(true);
    const result = await checkSupportLicense(merchantWallet);
    setHasAccess(result.hasAccess);
    setError(result.error || null);
    setIsLoading(false);
  }, [merchantWallet]);

  useEffect(() => { check(); }, [check]);

  const purchase = useCallback(async () => {
    const result = await purchaseSupportModule(merchantWallet);
    if (result.success) {
      setHasAccess(true);
      setError(null);
    } else {
      setError(result.error);
    }
    return result;
  }, [merchantWallet]);

  return { hasAccess, isLoading, error, refresh: check, purchase };
}

/**
 * Gate component: renders children if licensed, otherwise shows a purchase prompt.
 *
 * Props:
 *   merchantWallet - wallet address to check
 *   children       - the support UI to render when licensed
 *   bypass         - if true, skip license check (dev mode)
 */
export default function SupportLicenseGate({ merchantWallet, children, bypass }) {
  const { hasAccess, isLoading, error, purchase } = useSupportLicense(merchantWallet);
  const [purchasing, setPurchasing] = useState(false);

  // Dev bypass
  if (bypass) return <>{children}</>;

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.spinner}>Checking support license...</div>
      </div>
    );
  }

  if (hasAccess) return <>{children}</>;

  // Paywall
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Support Ticketing System</h2>
        <p style={styles.subtitle}>Unlock enterprise support management for your events</p>
        <ul style={styles.features}>
          {FEATURES.map((f) => (
            <li key={f} style={styles.feature}>{f}</li>
          ))}
        </ul>
        {error && <p style={styles.error}>{error}</p>}
        <button
          style={styles.button}
          disabled={purchasing}
          onClick={async () => {
            setPurchasing(true);
            await purchase();
            setPurchasing(false);
          }}
        >
          {purchasing ? 'Purchasing...' : 'Purchase — 150 PGT'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: '2rem', minHeight: 300,
  },
  spinner: { color: '#888', fontSize: '0.9rem' },
  card: {
    background: '#1e1e1e', border: '1px solid #333', borderRadius: 12,
    padding: '2rem', maxWidth: 420, width: '100%', textAlign: 'center',
  },
  title: { color: '#fff', margin: '0 0 0.5rem', fontSize: '1.3rem' },
  subtitle: { color: '#aaa', margin: '0 0 1.2rem', fontSize: '0.9rem' },
  features: { textAlign: 'left', padding: '0 1rem', margin: '0 0 1.5rem', listStyle: 'none' },
  feature: {
    color: '#ccc', fontSize: '0.85rem', padding: '0.3rem 0',
    borderBottom: '1px solid #2a2a2a',
  },
  error: { color: '#ef4444', fontSize: '0.8rem', margin: '0 0 0.75rem' },
  button: {
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: '#fff', border: 'none', borderRadius: 8,
    padding: '0.7rem 1.5rem', fontSize: '0.95rem', fontWeight: 600,
    cursor: 'pointer', width: '100%',
  },
};
